/**
 * useCreateOpportunity Hook
 * 
 * Application layer hook for creating new opportunities.
 * Handles URL parsing, geocoding, API calls, and parcel data fetching.
 * Uses existing geocoding and parcel infrastructure.
 */

import { useState, useCallback } from 'react';
import { authApi, GeocodeResult, PropertyLookupResult } from '../shared/utils/api';
import { CreateOpportunityRequest, Opportunity } from '../shared/types/opportunity';
import { logger } from '../shared/utils/logger';
import { parseListingUrl, normalizeUrl, generateOpportunityName, ParsedListing } from './createOpportunityUtils';

// =============================================================================
// Types
// =============================================================================

export interface OpportunityInput {
  listingUrl?: string;
  coordinates?: { latitude: number; longitude: number };
  address?: string;
  name?: string;
  askingPrice?: number;
  acreage?: number;
  notes?: string;
}

interface UseCreateOpportunityResult {
  create: (portfolioId: number, input: OpportunityInput) => Promise<Opportunity | null>;
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useCreateOpportunity(): UseCreateOpportunityResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const create = useCallback(async (
    portfolioId: number,
    input: OpportunityInput
  ): Promise<Opportunity | null> => {
    if (!portfolioId || portfolioId <= 0) {
      setError('Invalid portfolio');
      return null;
    }

    if (!input.listingUrl && !input.coordinates && !input.address) {
      setError('Please provide a listing URL, address, or location');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      let propertyLookupResult: PropertyLookupResult | null = null;
      let geocodeResult: GeocodeResult | null = null;
      let parsedListing: ParsedListing | null = null;
      let addressToGeocode: string | null = null;

      // Step 1: Parse URL if provided
      if (input.listingUrl) {
        const normalizedUrl = normalizeUrl(input.listingUrl);
        logger.debug('Parsing listing URL', 'create-opportunity', { normalized: normalizedUrl });
        parsedListing = parseListingUrl(normalizedUrl);
        
        if (parsedListing?.extractedAddress) {
          addressToGeocode = parsedListing.extractedAddress;
          logger.debug('Extracted address from URL', 'create-opportunity', { address: addressToGeocode });
        }
      }

      // Step 2: Geocode address (from URL extraction or manual input)
      if (input.address) addressToGeocode = input.address;

      if (addressToGeocode) {
        logger.debug('Geocoding address', 'create-opportunity', { address: addressToGeocode });
        try {
          propertyLookupResult = await authApi.propertyLookup({ address: addressToGeocode });
          if (!propertyLookupResult?.success) {
            geocodeResult = await authApi.geocodeAddress(addressToGeocode);
          }
        } catch (geoErr) {
          logger.warn('Geocode error, proceeding without coordinates', 'create-opportunity');
        }
      }

      // Step 3: Reverse geocode if only coordinates provided
      if (input.coordinates && !propertyLookupResult?.success && !geocodeResult?.success) {
        logger.debug('Reverse geocoding coordinates', 'create-opportunity', input.coordinates);
        try {
          geocodeResult = await authApi.reverseGeocode(input.coordinates.latitude, input.coordinates.longitude);
        } catch (geoErr) {
          logger.warn('Reverse geocode error', 'create-opportunity');
        }
      }

      // Step 4: Build request from results
      const propData = propertyLookupResult?.property_data;
      const geoData = propertyLookupResult?.geocode || geocodeResult;
      
      const streetAddress = propData?.address || geoData?.address?.street_address || null;
      const city = propData?.city || geoData?.address?.city || null;
      const state = propData?.state || geoData?.address?.state_abbr || geoData?.address?.state || null;
      const zipCode = propData?.zip_code || geoData?.address?.zip_code || null;
      const county = propData?.county || geoData?.address?.county || null;
      const latitude = propData?.latitude || geoData?.coordinates?.latitude || input.coordinates?.latitude || null;
      const longitude = propData?.longitude || geoData?.coordinates?.longitude || input.coordinates?.longitude || null;
      const formattedAddress = propData?.formatted_address || geoData?.formatted_address || null;

      const request: CreateOpportunityRequest = {
        name: generateOpportunityName({ 
          inputName: input.name, streetAddress, formattedAddress, 
          addressToGeocode, parsedListing, coordinates: input.coordinates 
        }),
        status: 'evaluating',
        street_address: streetAddress,
        city, state, zip_code: zipCode, county, latitude, longitude,
      };

      if (parsedListing) {
        request.listing_url = parsedListing.url;
        request.source_type = parsedListing.source;
        request.source_url = parsedListing.url;
      }

      if (input.coordinates && !request.latitude) {
        request.latitude = input.coordinates.latitude;
        request.longitude = input.coordinates.longitude;
      }

      if (input.askingPrice) request.asking_price = input.askingPrice;
      if (input.acreage) request.acreage = input.acreage;
      if (input.notes) request.description = input.notes;

      logger.debug('Creating opportunity', 'create-opportunity', { portfolioId, name: request.name });

      // Step 5: Create the opportunity
      const response = await authApi.createOpportunity(portfolioId, request);
      
      if (!response.success || !response.opportunity) {
        setError('Failed to create opportunity');
        return null;
      }

      const opportunity = response.opportunity;
      logger.info('Opportunity created', 'create-opportunity', { id: opportunity.id });

      // Step 6: Fetch parcel data from NC OneMap (background, don't block)
      if (opportunity.latitude && opportunity.longitude) {
        fetchParcelData(opportunity.id).catch(err => {
          logger.warn('Parcel data fetch failed (non-blocking)', 'create-opportunity', String(err));
        });
      }

      return opportunity;
    } catch (err: unknown) {
      let message = 'Failed to create opportunity';
      if (err instanceof Error) {
        const apiError = err as any;
        if (apiError.data?.message) message = apiError.data.message;
        else if (apiError.data?.error) message = apiError.data.error;
        else if (apiError.status === 403) message = 'No permission to add to this portfolio';
        else if (apiError.status === 401) message = 'Please log in again';
        else if (apiError.status === 500) message = 'Server error - try again later';
        else message = err.message;
      }
      logger.error('Create opportunity failed', err instanceof Error ? err : undefined, 'create-opportunity');
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { create, loading, error, clearError };
}

/**
 * Fetch parcel data for an opportunity (runs in background)
 * Calls NC OneMap to get parcel details like owner, acreage, tax value, etc.
 */
async function fetchParcelData(opportunityId: number): Promise<void> {
  try {
    logger.debug('Fetching parcel data for opportunity', 'create-opportunity', { opportunityId });
    
    const result = await authApi.linkPropertyToParcel(opportunityId, {
      auto_link: true,
      include_neighbors: false, // Don't fetch neighbors for now
      max_tier: 0,
    });
    
    if (result.success) {
      logger.info('Parcel data linked to opportunity', 'create-opportunity', {
        opportunityId,
        parcelId: result.primaryParcel?.parcel_id,
      });
    } else {
      logger.warn('No parcel found for opportunity', 'create-opportunity', {
        opportunityId,
        message: result.message,
      });
    }
  } catch (err) {
    // Non-blocking error - log but don't throw
    logger.warn('Parcel lookup failed', 'create-opportunity', String(err));
  }
}
